import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, adminSession, storeAppUrl } from '../../services/adminApi';
import { GlassButton, GlassInput } from '../../components/GlassUI';
import { FaShieldAlt, FaGem, FaEnvelope, FaLock, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already logged in? Straight to the console.
  useEffect(() => {
    if (adminSession.isLoggedIn()) {
      navigate('/overview', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await adminService.login(email.trim(), password);
      navigate('/overview', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-slate-950 relative overflow-hidden flex items-center justify-center p-4'>
      {/* Premium background glows */}
      <div className='absolute -top-32 -left-32 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl' />
      <div className='absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl' />
      <div className='absolute inset-0 opacity-[0.04]'
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className='relative w-full max-w-md'>
        {/* Brand */}
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-900/40 mb-4'>
            <FaShieldAlt className='text-white text-2xl' />
          </div>
          <h1 className='text-3xl font-bold text-white flex items-center justify-center gap-2'>
            <FaGem className='text-amber-400 text-xl' /> ApniCRM
          </h1>
          <p className='text-amber-200/80 font-semibold tracking-wide uppercase text-xs mt-1'>
            Admin Control Panel
          </p>
          <p className='text-slate-400 text-sm mt-2'>
            Manage accounts, plans, renewals & backups
          </p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className='bg-white/95 backdrop-blur-xl rounded-3xl p-7 md:p-8 shadow-2xl border border-white/40'
        >
          <h2 className='text-xl font-bold text-slate-900 mb-1'>Admin Login</h2>
          <p className='text-sm text-slate-500 mb-6'>Only super-admin accounts can enter.</p>

          {error && (
            <div className='flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4'>
              <FaExclamationTriangle className='mt-0.5 shrink-0' />
              <span>{error}</span>
            </div>
          )}

          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                Admin Email
              </label>
              <div className='relative'>
                <FaEnvelope className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm' />
                <GlassInput
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='admin@apni.com'
                  className='!pl-11'
                  autoComplete='username'
                />
              </div>
            </div>
            <div>
              <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                Password
              </label>
              <div className='relative'>
                <FaLock className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm' />
                <GlassInput
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder='••••••••'
                  className='!pl-11'
                  autoComplete='current-password'
                />
              </div>
            </div>

            <GlassButton
              type='submit'
              variant='gold'
              className='w-full py-3'
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Enter Admin Panel'}
            </GlassButton>
          </div>

        </form>

        <a
          href={storeAppUrl()}
          className='mt-6 flex items-center justify-center gap-2 text-slate-400 hover:text-amber-300 text-sm transition-colors'
        >
          <FaArrowLeft size={12} /> Back to CRM store app
        </a>
      </div>
    </div>
  );
};

export default AdminLoginPage;
